import { cn, Tooltip } from '@heroui/react';
import {
  TrashIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import React, { useState } from 'react';

import { CustomButton } from '@components';
import type { SignedUrls } from '@models';

import SwipeableContainer from './SwipeableContainer';

type ImageCarouselProps = {
  height?: string;
  imagePathBeingDeleted: string;
  imageUrls: SignedUrls;
  width?: string;
  onDelete: (index: number) => void;
};

const ImageCarousel = ({
  height = 'h-64',
  imagePathBeingDeleted,
  imageUrls,
  onDelete,
  width = 'w-full',
}: ImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const handleNext = () => {
    if (imageUrls.length <= 1) {
      return;
    }

    setDirection(1);
    setCurrentIndex((prevIndex) => {
      return (prevIndex + 1) % imageUrls.length;
    });
  };

  const handlePrev = () => {
    if (imageUrls.length <= 1) {
      return;
    }

    setDirection(-1);
    setCurrentIndex((prevIndex) => {
      return (prevIndex - 1 + imageUrls.length) % imageUrls.length;
    });
  };

  const handleDelete = (index: number) => {
    if (!onDelete) {
      return;
    }

    onDelete(index);
  };

  if (imageUrls.length === 0) {
    return (
      <div
        className={`relative flex items-center justify-center ${width} ${height} bg-muted`}
      >
        <p className="text-gray-500">No images available</p>
      </div>
    );
  }

  const currentImageUrl = imageUrls[currentIndex];
  const { signedUrl } = currentImageUrl;

  return (
    <div className={`relative overflow-hidden ${width} ${height} bg-muted`}>
      <SwipeableContainer
        direction={direction}
        onSwipeLeft={handleNext}
        onSwipeRight={handlePrev}
        className="absolute inset-0"
        swipeKey={String(currentIndex)}
      >
        {!!currentImageUrl.error && (
          <div className="bg-danger absolute inset-0 flex items-center justify-center rounded-sm">
            <p className="text-white">Error loading image</p>
          </div>
        )}
        {!!currentImageUrl.signedUrl && (
          <img
            draggable={false}
            alt={`Slide ${currentIndex}`}
            src={currentImageUrl.signedUrl}
            className="h-full w-full rounded-lg object-cover select-none"
          />
        )}
      </SwipeableContainer>

      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-between p-4">
        <CustomButton
          size="sm"
          isIconOnly
          variant="primary"
          onPress={handlePrev}
          isDisabled={imageUrls.length <= 1}
          className="pointer-events-auto rounded-full"
        >
          <CaretLeftIcon size={20} weight="bold" />
        </CustomButton>

        <CustomButton
          size="sm"
          isIconOnly
          variant="primary"
          onPress={handleNext}
          isDisabled={imageUrls.length <= 1}
          className="pointer-events-auto rounded-full"
        >
          <CaretRightIcon size={20} weight="bold" />
        </CustomButton>
      </div>

      {signedUrl && (
        <Tooltip closeDelay={0}>
          <Tooltip.Trigger>
            <div className="pointer-events-auto absolute top-4 right-14 z-10">
              <CustomButton
                size="sm"
                isIconOnly
                variant="secondary"
                className="rounded-full"
                onPress={() => {
                  window.open(signedUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                <ArrowSquareOutIcon size={18} weight="bold" />
              </CustomButton>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Content>Open this photo in a new tab</Tooltip.Content>
        </Tooltip>
      )}

      <Tooltip closeDelay={0}>
        <Tooltip.Trigger>
          <div className="pointer-events-auto absolute top-4 right-4 z-10">
            <CustomButton
              size="sm"
              isIconOnly
              variant="danger"
              className="rounded-full"
              isPending={imagePathBeingDeleted === currentImageUrl.path}
              onPress={() => {
                handleDelete(currentIndex);
              }}
            >
              {({ isPending }) => {
                if (isPending) {
                  return null;
                }

                return <TrashIcon size={18} weight="bold" />;
              }}
            </CustomButton>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content>Delete this photo</Tooltip.Content>
      </Tooltip>

      <div className="absolute right-0 bottom-4 left-0 z-10">
        <div className="mx-auto flex w-max items-center justify-center gap-2 rounded-full bg-black/30 p-2 backdrop-blur-xs">
          {imageUrls.map((_, index) => {
            const isActive = currentIndex === index;

            return (
              <CustomButton
                key={index}
                type="button"
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className={cn(
                  'h-2 rounded-full shadow-xs transition-all',
                  isActive && 'bg-white',
                  !isActive && 'bg-background-tertiary hover:bg-surface'
                )}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ImageCarousel;
